import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import type { ProtoFile } from './orders.ts'
import type { WarehouseName } from './warehouse.ts'

/**
 * Spec §12.1: the warehouse shows operations, never an editable balance. Every change is one
 * of these rows, and rows are never edited or deleted; a mistake is fixed with «Коригування».
 * `opening` is the prototype's starting balance before the seeded history.
 */
export type MoveKind =
  | 'opening'
  | 'receipt'
  | 'issue'
  | 'writeoff'
  | 'transfer_out'
  | 'transfer_in'
  | 'adjustment'
  | 'reserve'
  | 'release'

export const MOVE_LABELS: Record<MoveKind, string> = {
  opening: 'Залишок на початок',
  receipt: 'Надходження',
  issue: 'Видача',
  writeoff: 'Списання',
  transfer_out: 'Переміщення',
  transfer_in: 'Переміщення',
  adjustment: 'Коригування',
  reserve: 'Резерв',
  release: 'Вивільнення резерву',
}

export interface StockMove {
  id: string
  at: string
  kind: MoveKind
  materialId: string
  warehouse: WarehouseName
  location: string
  /** Signed, in the material's base unit. For reserve/release it moves the reservation. */
  quantity: string
  /** What the person typed, before conversion (spec §12.5 keeps both). */
  entered: { quantity: string; unit: string } | null
  actorId: string
  /** ЗМ number, supplier, document: what this move is about. */
  ref: string | null
  reason: string | null
  /** UAH per base unit, for receipts. */
  unitCost: string | null
  /** Pairs the two legs of a transfer. */
  correlationId: string | null
  buyerEntityId?: string | null
  supplier?: string | null
  files?: ProtoFile[]
}

/** Moves that change physical stock (reservations do not). */
export const PHYSICAL_KINDS: MoveKind[] = [
  'opening',
  'receipt',
  'issue',
  'writeoff',
  'transfer_out',
  'transfer_in',
  'adjustment',
]

export const STOCK_MOVES: StockMove[] = createMutable([] as StockMove[])
